import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/console/features/card-layout/components/card/card';
import { TEmptyCard } from '@/console/features/card-layout/components/card/card.types';
import { PipelineCardRow } from '@/console/features/card-layout/components/recent-pipelines-card/components/pipeline-card-row/pipeline-card-row';
import { LatestPipelineExecutionsProvider } from '@/console/features/pipelines/hooks/use-latest-pipeline-executions/latest-pipeline-executions.context';
import { PipelinesIcon } from '@/shared/assets/icons/pipelines-icon';
import { ShowIcon } from '@/shared/assets/icons/show-icon';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useBackgroundRefetch } from '@/shared/hooks/use-background-refetch/use-background-refetch';
import { useReadAllPipelinesQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  PaginationInput,
  PipelineConfigurationFragment,
} from '@/shared/types/types.generated';

export const RecentPipelinesCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pagination: PaginationInput = {
    page: 1,
    pageSize: 15,
  };
  const { data, isLoading, isFetching } = useReadAllPipelinesQuery({
    pagination,
  });
  const visibleIsFetching = useBackgroundRefetch(isFetching);
  const showLoader = isLoading || visibleIsFetching;

  const renderPopoverContent = () => {
    return (
      <>
        <MenuItem onClick={() => navigate('/pipelines')} icon={<ShowIcon />}>
          {t('card_layout:view_all_pipelines')}
        </MenuItem>
      </>
    );
  };

  const getEmptyCardProps = (): TEmptyCard => {
    return {
      action: <></>,
      icon: <PipelinesIcon />,
      text: t('card_layout:pipelines.empty_message'),
    };
  };

  const pipelines = data?.readAllPipelineConfigurations.items;

  const pipelineConfigurationIds = useMemo(
    () => pipelines?.map((pipeline) => pipeline.pipelineConfigurationId) ?? [],
    [pipelines],
  );

  return (
    <LatestPipelineExecutionsProvider
      pipelineConfigurationIds={pipelineConfigurationIds}
    >
      <Card<PipelineConfigurationFragment>
        title={t('card_layout:pipelines.title')}
        emptyCard={getEmptyCardProps()}
        data={pipelines}
        row={(pipeline) => (
          <PipelineCardRow
            pipeline={pipeline}
            key={pipeline.pipelineConfigurationId}
          />
        )}
        showLoader={showLoader}
        popoverContent={renderPopoverContent()}
      />
    </LatestPipelineExecutionsProvider>
  );
};
